import { computed } from 'vue';
import { IClient } from '@torim-app/types';
import { useAdminAvailabilityStore, useAdminStore } from '@app/stores';

export type BookableProvider = Pick<IClient, 'userId' | 'fullName' | 'profileImage'>;

/**
 * The employees whose calendars this user may work with — read them, and book on them.
 *
 * Managers get the whole team. An employee the owner granted the branch calendar to gets
 * their colleagues at the same branch(es); branch membership comes from the working-hours
 * blocks, and the server only sends them the hours of people they're allowed to see.
 * Everyone else gets an empty list, which the callers read as "just me, no picker".
 *
 * The server applies the same rule to every read and write, so this only decides what the
 * UI offers.
 */
export const useBookableProviders = () => {
  const adminStore = useAdminStore();
  const availabilityStore = useAdminAvailabilityStore();

  const bookableProviders = computed<BookableProvider[]>(() => {
    if (adminStore.getCanManageOthers) return adminStore.getProviders ?? [];
    if (!adminStore.getCanViewBranchCalendar) return [];

    const ownBranches = availabilityStore.getProviderBranchIds(adminStore.userId);
    return (adminStore.getProviders ?? []).filter(p =>
      p.userId === adminStore.userId
      || availabilityStore.getProviderBranchIds(p.userId).some(id => ownBranches.includes(id))
    );
  });

  /** Whether a booking may be placed on this provider's calendar. */
  const canBookFor = (providerId?: string): boolean =>
    !!providerId
    && (providerId === adminStore.userId || bookableProviders.value.some(p => p.userId === providerId));

  return { bookableProviders, canBookFor };
};
